/**
 * What the assistant costs, per tenant.
 *
 * ONE KEY SERVES EVERY TENANT, so without this the bill arrives as a single
 * number with no way to attribute it. That is the whole reason this exists:
 * not to shame a tenant, but to answer "is this one org, or all of them?" -
 * which decides whether the fix is a conversation or a code change.
 *
 * THE CACHE COLUMN IS THE INTERESTING ONE. Writing a cache entry costs about
 * 1.25x normal input and reading one about 0.1x, and entries live five
 * minutes. So caching pays only when questions arrive in conversations rather
 * than one at a time. Reads well above writes means it is working; writes
 * dominating reads means it is costing money and should come out.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

interface Bucket {
  calls: number;
  inputTokens: number;
  outputTokens: number;
  cacheWriteTokens: number;
  cacheReadTokens: number;
}

interface Row {
  orgId: string;
  name: string;
  thisMonth: Bucket;
  lastMonth: Bucket;
}

const n = (x: number): string => x.toLocaleString();

/** Total input actually paid for, in whole-price-equivalent tokens. */
function billableInput(b: Bucket): number {
  return b.inputTokens + Math.round(b.cacheWriteTokens * 1.25) + Math.round(b.cacheReadTokens * 0.1);
}

export function AiUsageView() {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [month, setMonth] = useState('');
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const r = await httpsCallable(functions, 'adminAiUsage')({});
        const d = r.data as { month: string; rows: Row[] };
        setRows(d.rows);
        setMonth(d.month);
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'Could not read usage.');
        setRows([]);
      }
    })();
  }, []);

  if (rows === null) return <div className="empty">Loading.</div>;

  const total = rows.reduce(
    (acc, r) => ({
      calls: acc.calls + r.thisMonth.calls,
      inputTokens: acc.inputTokens + r.thisMonth.inputTokens,
      outputTokens: acc.outputTokens + r.thisMonth.outputTokens,
      cacheWriteTokens: acc.cacheWriteTokens + r.thisMonth.cacheWriteTokens,
      cacheReadTokens: acc.cacheReadTokens + r.thisMonth.cacheReadTokens,
    }),
    { calls: 0, inputTokens: 0, outputTokens: 0, cacheWriteTokens: 0, cacheReadTokens: 0 },
  );

  const cacheWorking = total.cacheReadTokens > total.cacheWriteTokens;
  const busy = rows.filter((r) => r.thisMonth.calls > 0);

  return (
    <section className="panel">
      <div className="panel-title">Assistant usage — {month}</div>
      <p className="panel-note">
        The AI panel is the only thing in the product that spends Anthropic credits, and it only
        calls when somebody asks a question. Nothing here is on a schedule.
      </p>

      {err && <div className="banner banner-error">{err}</div>}

      <div className="stats">
        <div className="stat"><div className="n">{n(total.calls)}</div><div className="l">Questions</div></div>
        <div className="stat"><div className="n">{n(total.inputTokens)}</div><div className="l">Input tokens</div></div>
        <div className="stat"><div className="n">{n(total.outputTokens)}</div><div className="l">Output tokens</div></div>
        <div className="stat"><div className="n">{n(total.cacheReadTokens)}</div><div className="l">Cache reads</div></div>
        <div className="stat"><div className="n">{n(total.cacheWriteTokens)}</div><div className="l">Cache writes</div></div>
      </div>

      {total.calls > 0 && (
        <div className={'banner ' + (cacheWorking ? 'banner-ok' : 'banner-error')}>
          {cacheWorking
            ? 'Prompt caching is paying for itself — more is being read from cache than written to it.'
            : 'Prompt caching is NOT paying for itself yet. Writes cost about 1.25x and reads about 0.1x, '
              + 'so questions are arriving too far apart to hit the five-minute window. Worth removing if this holds.'}
        </div>
      )}

      {busy.length === 0 ? (
        <div className="empty">Nobody has asked the assistant anything this month.</div>
      ) : (
        <table className="mini-table">
          <thead>
            <tr>
              <th>Tenant</th><th>Questions</th><th>Input</th><th>Output</th>
              <th>Cache read</th><th>Billable input</th><th>Last month</th>
            </tr>
          </thead>
          <tbody>
            {busy.map((r) => (
              <tr key={r.orgId}>
                <td>{r.name}</td>
                <td>{n(r.thisMonth.calls)}</td>
                <td>{n(r.thisMonth.inputTokens)}</td>
                <td>{n(r.thisMonth.outputTokens)}</td>
                <td>{n(r.thisMonth.cacheReadTokens)}</td>
                {/* What was actually paid for, once the cache discount and the
                    write premium are applied - which is the number that
                    corresponds to the invoice. */}
                <td>{n(billableInput(r.thisMonth))}</td>
                <td className="dim">{n(r.lastMonth.calls)} questions</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
